import { useMutation } from "@tanstack/react-query";
import { useState, type FC } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Alert } from "@privacy/ui";
import Divider from "@mui/material/Divider";
import { useTranslation } from "@privacy/translations";
import { Drawer } from "../../../shared/Drawer";
import { DynamicForm } from "../../../dynamicForm";
import {
  prefetchDpiaRecommendedForm,
  setConnectedTenantsMutationOptions,
  prefetchRegistrationUsersWithAccess,
  useGetRegistrationById,
  useGetRegistrationForm,
  useGetRegistrationUsersWithAccessCount,
  useSetResponsible,
  useUpdateRegistrationAnswers,
  useUpdateRegistrationStatus,
} from "../../../api/registrations";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import {
  isUnansweredMandatoryQuestionsError,
  processorOf,
  type RegistrationById,
} from "../../../api/registrations.types";
import { usePermission } from "../../../shared/permissions";
import { PersonIcon1, DpiaIcon1 } from "../../../shared/icons";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { DynamicGrid } from "../../../dynamicGrid";
import { RegistrationStatusSelect } from "../RegistrationStatusSelect";
import { trackEvent } from "../../../shared/Snowplow";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { ConnectedTenants } from "./ConnectedTenants";
import { ConnectedProcessor } from "./ConnectedProcessor";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";
import { RegistrationResponsible } from "./RegistrationResponsible";
import { DateTimeInfo } from "./DateTimeInfo";
import { useHasPermissionToUpdateAnswers, useHasPermissionToUpdateMetaData } from "./useHasPermissionToUpdate";
import { RegistrationMinorOrMajorChangeDialog } from "./RegistrationMinorOrMajorChangeDialog";
import { DpiaRecommendedSubDrawerTitle } from "./DpiaRecommendedSubDrawer";

export const RegistrationInlineFormDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { navigate } = usePrivacyNavigate();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const usersWithAccessCountQuery = useGetRegistrationUsersWithAccessCount(registrationId, variant);
  const hasPermissionToUpdateAnswers = useHasPermissionToUpdateAnswers();
  const hasPermissionToUpdateMetaData = useHasPermissionToUpdateMetaData();
  const registrationAnswersMutation = useUpdateRegistrationAnswers();
  const setResponsibleMutation = useSetResponsible(registrationId);
  const setConnectedTenantsMutation = useMutation(setConnectedTenantsMutationOptions(variant));
  const registrationsFormQuery = useGetRegistrationForm(variant);
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);
  const [isMajorOrMinorChangeDialogOpen, setIsMajorOrMinorChangeDialogOpen] = useState(false);

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registrationByIdQuery.data || !registrationsFormQuery.data) return <Drawer.Loading />;

        const registration = registrationByIdQuery.data;
        return (
          <>
            <DynamicForm.Provider
              key={registration.id}
              onSave={answer => {
                if (registration.status.status === "done") setIsMajorOrMinorChangeDialogOpen(true);
                return registrationAnswersMutation.mutateAsync(
                  { registrationId: registration.id, answers: [answer] },
                  { onError: () => setIsMajorOrMinorChangeDialogOpen(false) }
                );
              }}
              disableQuestionsByTag={{ [DATE_OF_NEXT_AUDIT_TAG]: registration.status.status === "audit" }}
              answers={registration.answers}
              isReadOnly={!hasPermissionToUpdateAnswers(registration)}
              isSaveError={
                registrationAnswersMutation.isError ||
                setResponsibleMutation.isError ||
                setConnectedTenantsMutation.isError
              }
              isSaving={
                registrationAnswersMutation.isPending ||
                setResponsibleMutation.isPending ||
                setConnectedTenantsMutation.isPending
              }
              mandatoryQuestionErrorMessage={
                isUnansweredMandatoryQuestionsError(updateStatusMutation.error)
                  ? t("RegistrationDrawer.UnansweredMandatoryQuestion.Input")
                  : undefined
              }
            >
              {({ formState }) => (
                <>
                  <RegistrationDrawerHeader
                    activeTab="standard"
                    registration={registration}
                    statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                    bottomSlot={
                      <Box>
                        <Divider />
                        <Stack sx={{ pb: 2, pt: 3, px: Drawer.PADDING_X, gap: 2 }}>
                          <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                            <Stack spacing={0.5}>
                              <DateTimeInfo
                                label={t("RegistrationGrid.CreatedAtHeader")}
                                datetime={registration.createdAt}
                              />
                              <DateTimeInfo
                                label={t("RegistrationGrid.UpdatedAtHeader")}
                                datetime={registration.updatedAt}
                              />
                              {registration.origin && (
                                <Typography
                                  sx={{
                                    fontSize: theme => theme.typography.pxToRem(12),
                                    color: theme => theme.palette.text.secondary,
                                  }}
                                >
                                  {t("Registration.Origin.ReportedBy", { email: registration.origin.reporterEmail })}
                                </Typography>
                              )}
                            </Stack>
                            <RegistrationStatusSelect
                              registration={registration}
                              updateStatusMutation={updateStatusMutation}
                            />
                          </Stack>
                          <RegistrationAlert registration={registration} />
                        </Stack>
                      </Box>
                    }
                  />
                  <Drawer.Content sx={{ pt: 3 }}>
                    {registration.organisationEditability?.isCustomerManaged && (
                      <Alert sx={{ mb: 3 }} severity="info">
                        {t("RegistrationGrid.Drawer.InfoAlert.CustomerManaged")}
                      </Alert>
                    )}
                    <Stack sx={{ mb: 5 }}>
                      {variant === "Processor" && currentCustomer.customerKind !== "enterprise" && (
                        <ConnectedProcessor name={processorOf(registration)?.name} />
                      )}
                      <ConnectedTenants
                        isReadOnly={!hasPermissionToUpdateMetaData(registration)}
                        registration={registration}
                        setConnectedTenantsMutation={setConnectedTenantsMutation}
                      />
                      <RegistrationResponsible
                        isReadOnly={!hasPermissionToUpdateMetaData(registration)}
                        registration={registration}
                        setResponsibleMutation={setResponsibleMutation}
                      />
                      <Drawer.GoToSubDrawerButtonsWrapper>
                        <Drawer.GoToSubDrawerButton
                          Icon={PersonIcon1}
                          label={t("RegistrationDrawer.UsersWithAccessButton")}
                          count={usersWithAccessCountQuery.data?.numberOfUsersWithAccess}
                          onClick={() => navigate("users-with-access")}
                          onMouseOver={() => prefetchRegistrationUsersWithAccess(registrationId, variant)}
                        />
                        {variant === "Controller" && (
                          <Drawer.GoToSubDrawerButton
                            Icon={DpiaIcon1}
                            label={
                              <DpiaRecommendedSubDrawerTitle
                                sx={theme => ({ fontSize: theme.typography.pxToRem(14) })}
                                autoDPIAPreAssessment={registration.autoDPIAPreAssessment}
                              />
                            }
                            onMouseOver={() => prefetchDpiaRecommendedForm(variant)}
                            onClick={() => navigate("dpia-recommended")}
                          />
                        )}
                      </Drawer.GoToSubDrawerButtonsWrapper>
                    </Stack>

                    <Drawer.Divider />
                    <Stack sx={{ my: 5 }}>
                      <DynamicForm.InlineForm id={registration.id} form={registrationsFormQuery.data} />
                    </Stack>
                  </Drawer.Content>
                </>
              )}
            </DynamicForm.Provider>
            {isMajorOrMinorChangeDialogOpen && (
              <RegistrationMinorOrMajorChangeDialog
                answerRequestMutation={registrationAnswersMutation}
                onClose={() => setIsMajorOrMinorChangeDialogOpen(false)}
                registrationId={registration.id}
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

export const RegistrationMultiStepFormDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const hasPermissionToUpdateAnswers = useHasPermissionToUpdateAnswers();
  const registrationsFormQuery = useGetRegistrationForm(variant);
  const registrationAnswersMutation = useUpdateRegistrationAnswers();
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);
  const [isReviewOrDoneDialogOpen, setIsReviewOrDoneDialogOpen] = useState(false);

  return (
    <DynamicForm.MultiStepFormDialog>
      {(() => {
        if (!registrationByIdQuery.data || !registrationsFormQuery.data) return <Drawer.Loading />;

        const registration = registrationByIdQuery.data;
        return (
          <>
            <DynamicForm.Provider
              key={registration.id}
              onSave={answer => {
                trackEvent.answerQuestionRegistrationGuidedView(answer.type);
                if (registration.status.status === "done") setIsReviewOrDoneDialogOpen(true);
                return registrationAnswersMutation.mutateAsync(
                  { registrationId: registration.id, answers: [answer] },
                  { onError: () => setIsReviewOrDoneDialogOpen(false) }
                );
              }}
              answers={registration.answers}
              disableQuestionsByTag={{ [DATE_OF_NEXT_AUDIT_TAG]: registration.status.status === "audit" }}
              isReadOnly={!hasPermissionToUpdateAnswers(registration)}
              isSaveError={registrationAnswersMutation.isError}
              isSaving={registrationAnswersMutation.isPending}
              mandatoryQuestionErrorMessage={
                isUnansweredMandatoryQuestionsError(updateStatusMutation.error)
                  ? t("RegistrationDrawer.UnansweredMandatoryQuestion.Input")
                  : undefined
              }
            >
              {({ formState }) => (
                <DynamicForm.Form
                  header={
                    <RegistrationDrawerHeader
                      activeTab="guided"
                      registration={registration}
                      topRightSlot={
                        <RegistrationStatusSelect
                          registration={registration}
                          updateStatusMutation={updateStatusMutation}
                        />
                      }
                      bottomSlot={
                        <Box sx={{ pb: 3, pt: 1 }}>
                          <RegistrationAlert registration={registration} />
                        </Box>
                      }
                      statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                    />
                  }
                  form={registrationsFormQuery.data}
                />
              )}
            </DynamicForm.Provider>
            {isReviewOrDoneDialogOpen && (
              <RegistrationMinorOrMajorChangeDialog
                answerRequestMutation={registrationAnswersMutation}
                registrationId={registration.id}
                onClose={() => setIsReviewOrDoneDialogOpen(false)}
              />
            )}
          </>
        );
      })()}
    </DynamicForm.MultiStepFormDialog>
  );
};

const RegistrationAlert: FC<{ registration: RegistrationById }> = ({ registration }) => {
  const { t, formatList } = useTranslation();
  const { hasPermission } = usePermission();
  const hasReviewPermission = hasPermission("nova-forms-api:registration:review", registration.permissions);
  const mentionedRecords = registration.origin?.mentionedRecords ?? [];
  const isAwaitingOthersReview = registration.status.status === "review" && !hasReviewPermission;
  if (!isAwaitingOthersReview && mentionedRecords.length === 0) return null;

  return (
    <Stack sx={{ gap: 1 }}>
      {isAwaitingOthersReview && (
        <Alert sx={{ padding: "3px 16px" }} severity="info">
          {t("RegistrationGrid.Drawer.InfoAlert.Review")}
        </Alert>
      )}
      {mentionedRecords.length > 0 && (
        <Alert sx={{ padding: "3px 16px" }} severity="info">
          {t("Registration.Origin.MentionedRecords", { names: formatList(mentionedRecords) })}
        </Alert>
      )}
    </Stack>
  );
};

const DATE_OF_NEXT_AUDIT_TAG = "DATE_OF_NEXT_AUDIT";
